import { useMemo } from "react";
import {
  Avatar,
  AvatarGroup,
  SkeletonBlock,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";
import {
  useFolderSharing,
  HydratedFolderShare,
  useCurrentFolder,
} from "@zapier/client";

import { getInitials } from "@zapier/shared";

import style from "./FolderHeaderSharing.module.css";

const FolderHeaderSharing = () => {
  const folder = useCurrentFolder();
  const { shares, isSharesLoading } = useFolderSharing({
    folderId: folder?.id,
    rootId: folder?.rootId,
  });

  // In the folder view, don't show the owner again in the UI as a folder share, as it's redundant
  const filteredShares = useMemo(() => {
    return (
      shares &&
      Array.isArray(shares) &&
      shares.filter(
        (share: any) => (share?.member?.id || share.id) !== folder?.ownerId,
      )
    );
  }, [shares, folder?.ownerId]);

  if (!folder) {
    return null;
  }

  const ownerName = folder?.owner?.fullName || "";
  const ownerLabel = ownerName
    ? `${folder?.owner?.fullName} is the folder creator`
    : "";

  return (
    <div className={style.folderHeaderSharing}>
      {isSharesLoading ? (
        <SkeletonBlock borderRadius="large" height="36px" width="100px" />
      ) : (
        <AvatarGroup size="medium">
          <Tooltip allowMultiline content={ownerLabel} placement="left">
            <TooltipFocusableTrigger>
              <Avatar
                isBlock={true}
                name={ownerName}
                size="medium"
                url={folder?.owner?.photoUrl}
              />
            </TooltipFocusableTrigger>
          </Tooltip>
          <>
            {filteredShares &&
              filteredShares.map((share: HydratedFolderShare) => {
                const url =
                  share?.member?.photoUrl ||
                  share?.team?.logo ||
                  share?.account?.logoUrl ||
                  "";

                const name =
                  share?.member?.fullName ||
                  share?.team?.name ||
                  share?.account?.name;
                const sharedWithLabel = `Shared with ${name}`;

                return (
                  <Tooltip
                    allowMultiline
                    content={sharedWithLabel}
                    placement="left"
                    key={share.id}
                  >
                    <TooltipFocusableTrigger>
                      <Avatar
                        initials={getInitials(name)}
                        isBlock={true}
                        name={sharedWithLabel}
                        size="medium"
                        url={url}
                      />
                    </TooltipFocusableTrigger>
                  </Tooltip>
                );
              })}
          </>
        </AvatarGroup>
      )}
    </div>
  );
};

export default FolderHeaderSharing;
